import { useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { AllLeaguesStatus } from '@/components/league-navigator/screens/AllLeaguesScreen';
import { AllLeaguesItem } from '@/components/league-navigator/utils/all-leagues-utils';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { SbkLobbyParams } from '@/navigation/sweeps/types';
import { RootStackParamList } from '@/navigation/types';

import { buildAllLeaguesSections } from '../utils/all-leagues-utils';
import { findEntryForLeague } from '../utils/navigation-launchpad-utils';
import { NavigationEntry, useNavigationEntries } from './use-navigation-entries';
import { competitionTileId, sportTileId, useSbkLobbySectionSelection } from './use-sbk-lobby-section-selection';
import { useSports } from './use-sports';

type LobbyTarget = {
    params: SbkLobbyParams;
    selection?: { tile: string; subGroupId: string };
};

const getLobbyTarget = (entries: NavigationEntry[], leagueId: string): LobbyTarget => {
    const match = findEntryForLeague(entries, leagueId);
    if (!match?.subGroup) {
        return { params: { leagueId } };
    }
    switch (match.entry.type) {
        case 'sport':
            return {
                params: { sportId: match.entry.sport_id },
                selection: { tile: sportTileId(match.entry.sport_id), subGroupId: match.subGroup.id },
            };
        case 'competition':
            return {
                params: { competitionId: match.entry.id },
                selection: { tile: competitionTileId(match.entry.id), subGroupId: match.subGroup.id },
            };
        default:
            return { params: { leagueId } };
    }
};

export const useAllLeaguesScreen = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const isPredictions = useIsPredictionsProduct();
    const { data: sports, isLoading, isFetching, refetch } = useSports();
    const { data: entries } = useNavigationEntries();
    const setSelectedTile = useSbkLobbySectionSelection(state => state.actions.setSelectedTile);
    const setSelectedSubGroup = useSbkLobbySectionSelection(state => state.actions.setSelectedSubGroup);

    const sections = useMemo(() => buildAllLeaguesSections(sports ?? []), [sports]);

    const getStatus = (): AllLeaguesStatus => {
        if (isLoading) {
            return 'loading';
        }
        if (sections.length > 0) {
            return 'ready';
        }
        return isFetching ? 'retrying' : 'error';
    };

    const onRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const onSelectLeague = useCallback(
        (league: AllLeaguesItem) => {
            GASbkEvents.clickLeague(league.id, league.label);
            const { params, selection } = getLobbyTarget(entries ?? [], league.id);
            // the lobby re-sets the tile from its route params, which clears the chip, so the tile is set here first
            if (selection) {
                setSelectedTile(selection.tile);
                setSelectedSubGroup(selection.subGroupId);
            }
            if (isPredictions) {
                navigation.popTo('PredictionHome', {
                    screen: 'PredictionLobby',
                    params: { screen: 'PredictionLobbyScreen', params },
                });
                return;
            }
            navigation.popTo('SweepsHome', {
                screen: 'SweepsLobby',
                params: { screen: 'SweepsLobbyScreen', params },
            });
        },
        [entries, isPredictions, navigation, setSelectedSubGroup, setSelectedTile]
    );

    return { sections, status: getStatus(), onSelectLeague, onRetry };
};
